import { message } from 'antd'
import { pathOr } from 'ramda'
import dayjs from 'dayjs'
import { extractFiles } from 'components/Upload/helpers'
import { DATE_FORMAT_YMD } from 'utils/helpers/dateHelper'
import { SUBJECT_LOCKED_TICKET_TYPES } from 'constants/subjectTypeConstants'
import {
  Interaction_Modality_Code,
  InteractionSourceType
} from 'generated/types'

export const parseSymptom = symptomString => {
  const parsedSymptom = symptomString ? JSON.parse(symptomString) : null
  const symptomId = pathOr('', ['0'], parsedSymptom)
  const symptomName = pathOr('', ['1'], parsedSymptom)

  return { symptom_id: symptomId, symptom_name: symptomName }
}

const setTypeVariables = ({
  values,
  interactionVariables,
  ticketAttributes,
  selectedEntity,
  timeSlots,
  ticketType
}) => {
  if (!interactionVariables) return

  const {
    isAppointment,
    isBilling,
    isFindCare,
    isDiabetesProgram,
    isAnnualPhysical,
    isDrugSearch,
    isSeldon,
    isMedical,
    isFinancialProgram,
    isSpecialtyCostPlusConversion,
    isPbmContactCenter,
    isOverride,
    isPriorAuthorization,
    isWeightLossProgram,
    isBenefitInquiry,
    isDrugListUpdate,
    isPriorAuthorizationStatus,
    isAccumulatorIssue,
    isVendorSetup,
    isNewImplementation,
    isGroupTerming,
    isClaimFileProcessing,
    isImpactReport,
    isAccumulatorChange,
    isFertilityMaximum,
    isGroupNumberChange,
    isPlanChangeHandling,
    isEligibilityInquiry
  } = ticketType

  let variables = { ...interactionVariables }

  const {
    attachments,
    request_notes,
    focus_request_other,
    first_time_visit,
    patient_name,
    smoking_attestation,
    screen_date,
    title,
    notes,
    initial_value,
    final_value,
    symptom_name: symptom,
    initial_care_choice_ids,
    recommended_care_choice_ids
  } = values

  if (isDrugSearch) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: 'ProactiveDrug',
        proactive_drug_related: {
          categories: values?.categories
        }
      }
    }
  }

  if (isAccumulatorIssue) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: 'AccumulatorIssue',
        accumulator_issue_related: {
          request_type: values?.request_type,
          description: values?.description
        }
      }
    }
  }

  if (isVendorSetup) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: 'VendorSetup',
        vendor_setup_related: {
          changes_type: values?.changes_type,
          client_name: values?.client_name,
          contact_email: values?.contact_email,
          contact_name: values?.contact_name,
          implementation_date: values?.implementation_date.toISOString(),
          layout_type: values?.layout_type,
          sftp: values?.sftp,
          vendor_name: values?.vendor_name,
          vendor_type: values?.vendor_type
        }
      }
    }
  }

  if (isNewImplementation) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: 'NewImplementation',
        new_implementation_related: {
          accumulator_contact_email: values?.accumulator_contact_email,
          accumulator_contact_name: values?.accumulator_contact_name,
          accumulator_contact_phone: values?.accumulator_contact_phone,
          accumulator_layout_type: values?.accumulator_layout_type,
          accumulator_processing_method: values?.accumulator_processing_method,
          accumulator_reconciliation_file:
            values?.accumulator_reconciliation_file,
          accumulator_response_file: values?.accumulator_response_file,
          accumulator_vendor_name: values?.accumulator_vendor_name,
          claim_contact_email: values?.claim_contact_email,
          claim_contact_name: values?.claim_contact_name,
          claim_contact_phone: values?.claim_contact_phone,
          claim_layout_type: values?.claim_layout_type,
          claim_vendor_name: values?.claim_vendor_name,
          client_name: values?.client_name,
          eligibility_contact_email: values?.eligibility_contact_email,
          eligibility_contact_name: values?.eligibility_contact_name,
          eligibility_contact_phone: values?.eligibility_contact_phone,
          eligibility_layout_type: values?.eligibility_layout_type,
          eligibility_production_file_date:
            values?.eligibility_production_file_date?.toISOString(),
          eligibility_vendor_name: values?.eligibility_vendor_name,
          employee_id: values?.employee_id,
          expected_go_live_date: values?.expected_go_live_date?.toISOString(),
          id_card_type: values?.id_card_type,
          implementation_manager: values?.implementation_manager,
          jira_ticket_links: values?.jira_ticket_links,
          sftp: values?.sftp
        }
      }
    }
  }

  if (isGroupTerming) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: 'GroupTerming',
        group_terming_related: {
          client_name: values?.client_name,
          engineering_status: values?.engineering_status,
          jira_ticket_link: values?.jira_ticket_link,
          requestor: values?.requestor,
          run_out_date: values?.run_out_date.toISOString(),
          special_instruction: values?.special_instruction,
          term_date: values?.term_date.toISOString(),
          termination_reason: values?.termination_reason
        }
      }
    }
  }

  if (isClaimFileProcessing) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: 'ClaimFileProcessing',
        claim_file_processing_related: {
          client_name: values?.client_name,
          employee_id: values?.employee_id,
          existing_vendor: values?.existing_vendor,
          issue_description: values?.issue_description,
          jira_ticket_link: values?.jira_ticket_link,
          request_source: values?.request_source,
          request_type: values?.request_type,
          sftp: values?.sftp,
          vendor_contact_email: values?.vendor_contact_email,
          vendor_contact_name: values?.vendor_contact_name,
          vendor_contact_phone: values?.vendor_contact_phone,
          vendor_name: values?.vendor_name,
          vendor_type: values?.vendor_type
        }
      }
    }
  }

  if (isImpactReport) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: InteractionSourceType.ImpactReport,
        impact_report_related: {
          client_name: values?.client_name,
          client_owner: values?.client_owner,
          client_status: values?.client_status,
          end_date: values?.end_date?.toISOString(),
          impact_amount: values?.impact_amount,
          invoice_amount: values?.invoice_amount,
          issue_description: values?.issue_description,
          issue_type: values?.issue_type,
          jira_ticket_link: values?.jira_ticket_link,
          payment_status: values?.payment_status,
          start_date: values?.start_date?.toISOString()
        }
      }
    }
  }

  if (isAccumulatorChange) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: InteractionSourceType.AccumulatorChange,
        accumulator_change_related: {
          client_name: values?.client_name,
          description: values?.description,
          effective_date: values?.effective_date?.toISOString(),
          accumulator_changes: values?.accumulator_changes,
          groups_affected: values?.groups_affected,
          groups_id_cards: values?.groups_id_cards,
          reprint_scope: values?.reprint_scope
        }
      }
    }
  }

  if (isEligibilityInquiry) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: InteractionSourceType.EligibilityInquiry,
        eligibility_inquiry_related: {
          description: values?.description,
          eligibility_inquiry_issues_attributes:
            values?.eligibility_inquiry_issues_attributes || [],
          eligibility_inquiry_members_attributes:
            values?.eligibility_inquiry_members_attributes?.map(member => {
              const memberData = {
                name: member.name,
                dob: member.dob,
                cardholder_id: member.cardholder_id,
                coverage_start_date: member.coverage_start_date,
                group_id: member.group_id,
                plan_name: member.plan_name,
                reason_for_manual_add: member.reason_for_manual_add,
                new_cardholder_id_needed: member.new_cardholder_id_needed
              }
              // Preserve id for existing members
              if (member.id) memberData.id = member.id
              return memberData
            }) || []
        }
      }
    }
  }

  if (isFertilityMaximum) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: InteractionSourceType.FertilityMaximum,
        fertility_maximum_related: {
          client_name: values?.client_name,
          description: values?.description,
          effective_date: values?.effective_date?.toISOString(),
          adc_code_name: values?.adc_code_name,
          adc_code_description: values?.adc_code_description
        }
      }
    }
  }

  if (isGroupNumberChange) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: InteractionSourceType.GroupNumberChange,
        group_number_change_related: {
          client_name: values?.client_name,
          description: values?.description,
          effective_date: values?.effective_date?.toISOString(),
          action_type: values?.action_type,
          group_numbers_affected: values?.group_numbers_affected,
          hierarchy_update: values?.hierarchy_update
        }
      }
    }
  }

  if (isPlanChangeHandling) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: InteractionSourceType.PlanChangeHandling,
        plan_change_handling_related: {
          client_name: values?.client_name,
          description: values?.description,
          effective_date: values?.effective_date?.toISOString()
        }
      }
    }
  }

  if (isPbmContactCenter) {
    const RxCommonFields = {
      source_type: 'RxCommon',
      rx_common_related: {
        claim_id: values.claim_id,
        medication: values.medication
      }
    }

    const RxOverrideFields = {
      source_type: 'RxOverride',
      rx_override_related: {
        claim_id: values.claim_id,
        medication: values.medication,
        requested_by: values.requested_by,
        status: values.status,
        reason: values.reason
      }
    }

    const RxPriorAuthorizationFields = {
      source_type: 'RxPriorAuthorization',
      rx_prior_authorization_related: {
        claim_id: values.claim_id,
        medication: values.medication,
        requested_by: values.requested_by,
        symptom_id: values.symptom_id,
        symptom_name: values.symptom_name,
        new_start_or_cot: values.new_start_or_cot,
        tried_and_failed_medications: values.tried_and_failed_medications,
        mhk_rx_case_number: values.mhk_rx_case_number
      }
    }

    variables = {
      ...interactionVariables,
      source_attributes: {
        ...(isOverride && RxOverrideFields),
        ...(isPriorAuthorization && RxPriorAuthorizationFields),
        ...(!isOverride && !isPriorAuthorization && RxCommonFields)
      }
    }
  }

  if (isBenefitInquiry) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: 'BenefitInquiry',
        benefit_inquiry_related: {
          question: values?.question,
          topic_area: values?.topic_area,
          config_change_required: values?.config_change_required,
          claim_ids: values?.claim_ids
        }
      }
    }
  }

  if (isPriorAuthorizationStatus) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: 'PriorAuthorizationStatus',
        prior_authorization_status_related: {
          claim_id: values?.claim_id,
          medication: values?.medication,
          requested_by: values?.requested_by,
          mhk_rx_case_number: values?.mhk_rx_case_number,
          pa_status: values?.pa_status
        }
      }
    }
  }

  if (isBilling) {
    const claim_attrs = {
      source_attributes: {
        source_type: 'Claim',
        claim_related: {
          claim_category: values?.claim_category,
          attachments: extractFiles(attachments),
          admin_notes: values?.admin_notes,
          request_notes,
          insurance_carrier: values?.insurance_carrier,
          insurance_member_id: values?.insurance_member_id,
          group_id: values?.group_id,
          focus_request: values?.focus_request,
          ...(focus_request_other && { focus_request_other })
        }
      }
    }

    variables = {
      ...interactionVariables,
      ...claim_attrs
    }
  }

  if (isFindCare) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: 'Roadmap',
        roadmap_related: {
          image_type: values?.image_type,
          preferred_address: values?.preferred_address,
          request_notes,
          severity_rating: values?.severity_rating,
          response_threshold: values?.response_threshold,
          first_time_visit,
          health_issue: values?.health_issue,
          special_needs: values?.special_needs,
          provider_name: values?.provider_name,
          preferred_distance: values?.preferred_distance
        }
      }
    }
  }

  if (isAppointment) {
    const { isProvider, isFacility, id } = selectedEntity

    let entity_type
    if (isProvider) entity_type = 'doctor'
    else if (isFacility) entity_type = 'facility'
    else {
      message.error('Please attach a provider or a facility')
      return false
    }

    if (!timeSlots.find(slot => !slot.delete)) {
      message.error('Please attach at least one time slot')
      return false
    }
    const slots = timeSlots.map(slot => slot.toObject)

    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: 'Appointment',
        appointment_related: {
          entity_id: id,
          entity_type,
          time_slots: slots,
          first_time_visit,
          request_notes,
          location_uuid: values?.location_uuid,
          preferred_location_zip: values?.preferred_location_zip
        }
      }
    }
  }

  if (isWeightLossProgram) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: 'WeightManagementProgram',
        weight_management_program_related: {
          medication_names: values?.medication_names,
          member_declined: values?.member_declined || false,
          enrollment_date: values?.enrollment_date,
          member_declined_reason: values?.member_declined_reason
        }
      }
    }
  }

  if (isDrugListUpdate) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: 'DrugListUpdateRequest',
        drug_list_update_request_related: {
          drug_list_name: values?.drug_list_name,
          medication_name: values?.medication_name,
          action: values?.action,
          ndc: values?.ndc,
          gpi: values?.gpi,
          date: values?.date
        }
      }
    }
  }

  if (isDiabetesProgram) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: 'DiabetesProgram',
        diabetes_program_related: {
          medication_names: values?.medication_names,
          comorbidities: values?.comorbidities,
          diabetes_type: values?.diabetes_type,
          app_downloaded: values?.app_downloaded,
          enrollment_date: values?.enrollment_date
        }
      }
    }
  }

  if (isAnnualPhysical) {
    const attach = pathOr(null, ['0'], extractFiles(attachments))

    if (!attach || !attach.file) {
      message.error('Please add an attachment')
      return false
    }

    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: 'AnnualPhysicalForm',
        annual_physical_form_related: {
          attachment: {
            file: attach.file,
            notes: attach.notes,
            title: attach.title
          },
          patient_name,
          mammogram: values?.mammogram,
          pap_smear: values?.pap_smear,
          prostate_screening: values?.prostate_screening,
          colon_screening: values?.colon_screening,
          smoking_attestation
        }
      }
    }
  }

  if (isSeldon) {
    const attach = pathOr(null, ['0'], extractFiles(attachments))

    if (!attach || !attach.file) {
      message.error('Please add an attachment')
      return false
    }

    const parsedScreenDate = dayjs(screen_date).format(DATE_FORMAT_YMD)

    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: 'SeldonBeattieForm',
        seldon_beattie_form_related: {
          attachment: {
            file: attach.file,
            notes: attach.notes,
            title: attach.title
          },
          patient_name,
          screen_date: parsedScreenDate,
          title,
          notes,
          height: values?.height,
          weight: values?.weight,
          body_mass_index: values?.body_mass_index,
          a1c: values?.a1c,
          blood_pressure: values?.blood_pressure,
          total_cholesterol: values?.total_cholesterol,
          ldl_cholesterol: values?.ldl_cholesterol,
          hdl_cholesterol: values?.hdl_cholesterol,
          triglycerides: values?.triglycerides,
          fasting_glucose: values?.fasting_glucose,
          smoking_attestation
        }
      }
    }
  }

  if (isFinancialProgram || isSpecialtyCostPlusConversion) {
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: 'FinancialDataValue',
        financial_data_value_related: {
          initial_value,
          final_value
        }
      }
    }
  }

  if (isMedical) {
    const { symptom_id, symptom_name } = parseSymptom(symptom)
    variables = {
      ...interactionVariables,
      source_attributes: {
        source_type: 'MedicalNavigation',
        medical_navigation_related: {
          ...(symptom_id && { symptom_id }),
          ...(symptom_name && { symptom_name }),
          ...(initial_care_choice_ids && { initial_care_choice_ids }),
          ...(recommended_care_choice_ids && { recommended_care_choice_ids })
        }
      }
    }
  }

  return { variables, ticketAttributes }
}

export const createNewTicket = ({
  values,
  memberId,
  assigned_to_id,
  priority,
  notes,
  userId,
  selectedEntity,
  timeSlots,
  ticketType
}) => {
  const {
    ticket_type_id,
    direction,
    interaction_modality,
    interacted_with,
    benefit_categories,
    assignment_notes,
    contact_name,
    contact_detail,
    chat_id
  } = values

  const isDefaultPhone = contact_detail === '+1 (___) ___-____'

  const ticketAttributes = {
    ticket_type_id,
    member_id: memberId,
    assigned_to_id,
    assignment_notes,
    priority,
    ...(notes && { notes }),
    ...(benefit_categories && { benefit_categories }),
    ...(chat_id && { chat_id })
  }

  const interactionVariables = {
    direction,
    interaction_modality,
    ...(contact_name && { contact_name }),
    ...(contact_detail && !isDefaultPhone && { contact_detail }),
    interacted_with,
    member_id: memberId,
    navigator_id: userId,
    ...(notes && { notes })
  }

  const { variables } = setTypeVariables({
    values,
    interactionVariables,
    ticketAttributes,
    selectedEntity,
    timeSlots,
    ticketType
  })

  return { variables, ticketAttributes }
}

export const editTicketType = ({
  values,
  initial_interaction,
  ticketId,
  memberId,
  userId,
  ticketAttributes,
  selectedEntity,
  timeSlots,
  ticketType
}) => {
  const {
    isAppointment,
    isBilling,
    isFindCare,
    isDiabetesProgram,
    isAnnualPhysical,
    isSeldon,
    isMedical,
    isFinancialProgram,
    isSpecialtyCostPlusConversion,
    isPbmContactCenterCommon,
    isOverride,
    isPriorAuthorization,
    isDrugListUpdate,
    isBenefitInquiry,
    isPriorAuthorizationStatus,
    isVendorSetup,
    isNewImplementation,
    isGroupTerming,
    isClaimFileProcessing,
    isImpactReport,
    isAccumulatorChange,
    isFertilityMaximum,
    isGroupNumberChange,
    isPlanChangeHandling,
    isAccumulatorIssue,
    isEligibilityInquiry,
    isWeightLossProgram
  } = ticketType

  let type = null

  if (isAppointment) type = Interaction_Modality_Code.Appointment
  if (isBilling) type = Interaction_Modality_Code.Claim
  if (isFindCare) type = Interaction_Modality_Code.CarePlan
  if (isDiabetesProgram) type = Interaction_Modality_Code.DiabetesProgram
  if (isAnnualPhysical) type = Interaction_Modality_Code.AnnualPhysicalForm
  if (isSeldon) type = Interaction_Modality_Code.SeldonBeattieForm
  if (isMedical) type = Interaction_Modality_Code.MedicalNavigation
  if (isFinancialProgram || isSpecialtyCostPlusConversion)
    type = Interaction_Modality_Code.FinancialDataValue
  if (isPbmContactCenterCommon) type = 'common' // not an actual subject
  if (isOverride) type = Interaction_Modality_Code.Override
  if (isPriorAuthorization) type = 'prior_authorization'
  if (isDrugListUpdate) type = Interaction_Modality_Code.DrugListUpdateRequest
  if (isBenefitInquiry) type = Interaction_Modality_Code.BenefitInquiry
  if (isPriorAuthorizationStatus)
    type = Interaction_Modality_Code.PriorAuthorizationStatus
  if (isVendorSetup) type = Interaction_Modality_Code.VendorSetup
  if (isNewImplementation) type = Interaction_Modality_Code.NewImplementation
  if (isGroupTerming) type = Interaction_Modality_Code.GroupTerming
  if (isClaimFileProcessing)
    type = Interaction_Modality_Code.ClaimFileProcessings
  if (isImpactReport) type = Interaction_Modality_Code.ImpactReport
  if (isAccumulatorChange) type = Interaction_Modality_Code.AccumulatorChange
  if (isFertilityMaximum) type = Interaction_Modality_Code.FertilityMaximum
  if (isGroupNumberChange) type = Interaction_Modality_Code.GroupNumberChange
  if (isPlanChangeHandling) type = Interaction_Modality_Code.PlanChangeHandling
  if (isAccumulatorIssue) type = Interaction_Modality_Code.AccumulatorIssue
  if (isEligibilityInquiry) type = Interaction_Modality_Code.EligibilityInquiry
  if (isWeightLossProgram)
    type = Interaction_Modality_Code.WeightManagementProgram

  const interactionVariables = {
    ticket_id: ticketId,
    direction: initial_interaction.direction,
    interaction_modality: type || ticketType?.code,
    member_id: memberId,
    navigator_id: userId,
    interacted_with: 'internal'
  }

  const { variables } = setTypeVariables({
    values,
    interactionVariables,
    ticketAttributes,
    selectedEntity,
    timeSlots,
    ticketType
  })

  return { variables }
}

export const surveyHasResponses = surveyData => {
  const questions = surveyData?.ticket_from_id?.survey?.questions?.nodes || []
  return questions.some(q => {
    const answers = q?.answers?.nodes || []
    return answers.some(a => a.answer_value !== null)
  })
}

export const shouldConfirmTypeChange = (
  surveyData,
  ticketType,
  newTypeSurveyId,
  newTypeEnableSurveys
) => {
  if (!surveyHasResponses(surveyData)) return false
  const currentSurveyId = surveyData?.ticket_from_id?.survey?.id
  // New type has surveys enabled with default survey → backend will replace
  if (newTypeEnableSurveys && newTypeSurveyId)
    return newTypeSurveyId !== currentSurveyId
  // New type has no active survey → backend deletes only if old type auto-assigned it
  return (
    ticketType?.enable_surveys &&
    ticketType?.survey_id != null &&
    ticketType.survey_id === currentSurveyId
  )
}

export const canEditTicketSubject = ticket => {
  if (!ticket) return false

  const { type, state, archived } = ticket

  if (archived || state?.isClosed) return false

  return !SUBJECT_LOCKED_TICKET_TYPES.includes(type?.code)
}
